import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Hourglass, Paperclip, XCircle } from 'lucide-react';

export type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'pending'
  | 'pending_with_proof'
  | 'pending_without_proof';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  className?: string;
  pulse?: boolean;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-700 border-gray-200',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  pending_with_proof: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending_without_proof: 'bg-orange-50 text-orange-700 border-orange-200',
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  icon,
  className = '',
  pulse = false,
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${pulse ? 'animate-pulse' : ''}
        ${className}
      `.trim()}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  );
}

// Helper para obtener el badge correcto según el estado de pago
export function getPaymentStatusBadge(
  status: string,
  hasProof?: boolean
): { variant: BadgeVariant; label: string; icon?: ReactNode } {
  if (status === 'pending') {
    if (hasProof) {
      return {
        variant: 'pending_with_proof',
        label: 'Con comprobante',
        icon: <Paperclip className="w-4 h-4" />,
      };
    }
    return {
      variant: 'pending_without_proof',
      label: 'Sin comprobante',
      icon: <Hourglass className="w-4 h-4" />,
    };
  }

  if (status === 'confirmed') {
    return { variant: 'success', label: 'Confirmado', icon: <CheckCircle2 className="w-4 h-4" /> };
  }

  if (status === 'failed') {
    return { variant: 'danger', label: 'Rechazado', icon: <XCircle className="w-4 h-4" /> };
  }

  return { variant: 'default', label: status, icon: <AlertCircle className="w-4 h-4" /> };
}
